import React from "react";

/* ─── Icon ─────────────────────────────────────────────────── */

interface IconProps {
  d: React.ReactNode;
  size?: number;
  stroke?: number;
  fill?: string;
  style?: React.CSSProperties;
}

export const Icon: React.FC<IconProps> = ({
  d,
  size = 14,
  stroke = 1.6,
  fill = "none",
  style,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: "0 0 auto", ...style }}
  >
    {typeof d === "string" ? <path d={d} /> : d}
  </svg>
);

/* ─── Confluence brand mark ────────────────────────────────── */

/**
 * Official Confluence logo mark (blue rounded square + white swirl),
 * cropped from Atlassian's attribution artwork. Self-contained brand
 * colors so it reads correctly on both light and dark themes. The
 * clip-path from the source art is dropped on purpose: the swirl sits
 * fully inside the square, and inlining it repeatedly would create
 * duplicate SVG ids on the page.
 */
export const ConfluenceMark: React.FC<{ size?: number; style?: React.CSSProperties }> = ({
  size = 16,
  style,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="72.9482 0 75.0002 75"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flex: "0 0 auto", ...style }}
    aria-hidden="true"
  >
    <path
      d="M72.9482 18.75C72.9482 8.39466 81.3429 0 91.6982 0H129.198C139.554 0 147.948 8.39466 147.948 18.75V56.25C147.948 66.6053 139.554 75 129.198 75H91.6982C81.3429 75 72.9482 66.6053 72.9482 56.25V18.75Z"
      fill="#1868DB"
    />
    <path
      d="M127.229 44.5783C115.656 38.9824 112.275 38.1462 107.399 38.1462C101.677 38.1462 96.8011 40.5261 92.445 47.2154L91.7298 48.3089C91.1447 49.2094 91.0146 49.531 91.0146 49.9169C91.0146 50.3028 91.2097 50.6244 91.9249 51.0747L99.2717 55.6414C99.6618 55.8987 99.9869 56.0273 100.312 56.0273C100.702 56.0273 100.962 55.8344 101.352 55.2555L102.523 53.4545C104.343 50.6887 105.968 49.7882 108.049 49.7882C109.869 49.7882 112.015 50.3028 114.681 51.5892L122.353 55.1912C123.133 55.5771 123.978 55.3841 124.368 54.4836L128.009 46.5079C128.399 45.6074 128.139 45.0285 127.229 44.5783ZM93.4853 30.492C105.058 36.0879 108.439 36.9241 113.315 36.9241C119.037 36.9241 123.913 34.5442 128.269 27.8549L128.984 26.7614C129.569 25.861 129.699 25.5394 129.699 25.1534C129.699 24.7675 129.504 24.4459 128.789 23.9957L121.442 19.4289C121.052 19.1716 120.727 19.043 120.402 19.043C120.012 19.043 119.752 19.2359 119.362 19.8148L118.192 21.6158C116.371 24.3816 114.746 25.2821 112.665 25.2821C110.845 25.2821 108.699 24.7675 106.033 23.4811L98.3615 19.8791C97.5813 19.4932 96.7361 19.6862 96.346 20.5867L92.7051 28.5624C92.315 29.4629 92.575 30.0418 93.4853 30.492Z"
      fill="white"
    />
  </svg>
);

/* ─── Icon library ─────────────────────────────────────────── */

export const I = {
  check: <Icon d="M4 12.5l5 5L20 6.5" />,
  chevR: <Icon d="M9 6l6 6-6 6" />,
  chevD: <Icon d="M6 9l6 6 6-6" />,
  chevU: <Icon d="M6 15l6-6 6 6" />,
  chevL: <Icon d="M15 6l-6 6 6 6" />,
  plus: <Icon d="M12 5v14M5 12h14" />,
  x: <Icon d="M6 6l12 12M18 6L6 18" />,
  play: <Icon d="M7 5l12 7-12 7V5z" fill="currentColor" stroke={0} />,
  refresh: (
    <Icon d="M3 12a9 9 0 0 1 15.5-6.4M21 3v6h-6M21 12a9 9 0 0 1-15.5 6.4M3 21v-6h6" />
  ),
  download: <Icon d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" />,
  upload: <Icon d="M12 21V9m0 0l-4 4m4-4l4 4M4 3h16" />,
  paperclip: (
    <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  fileText: (
    <Icon d={<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>} />
  ),
  search: (
    <Icon d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.4-4.4" />
  ),
  zoom: <Icon d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  pin: <Icon d="M12 17v5M9 3h6l-1 7 3 3H7l3-3-1-7z" />,
  grip: (
    <Icon
      d={
        <g>
          <circle cx="9" cy="6" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="6" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="9" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="9" cy="18" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="18" r="1.2" fill="currentColor" stroke="none" />
        </g>
      }
    />
  ),
  cog: (
    <Icon d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM19.4 13.6l1.6 1.2-2 3.4-1.9-.6a7.6 7.6 0 0 1-2.1 1.2L14.5 21h-5l-.5-2.2a7.6 7.6 0 0 1-2.1-1.2l-1.9.6-2-3.4 1.6-1.2a7.7 7.7 0 0 1 0-2.4L3 10l2-3.4 1.9.6a7.6 7.6 0 0 1 2.1-1.2L9.5 3.8h5l.5 2.2a7.6 7.6 0 0 1 2.1 1.2l1.9-.6 2 3.4-1.6 1.2c.1.4.1.8.1 1.2s0 .8-.1 1.2z" />
  ),
  doc: <Icon d="M7 3h7l4 4v14H7zM14 3v5h5M9 13h7M9 17h5M9 9h2" />,
  git: (
    <Icon
      d={
        <g>
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </g>
      }
    />
  ),
  confluence: <ConfluenceMark size={16} />,
  code: <Icon d="M8 18l-5-6 5-6M16 6l5 6-5 6M14 4l-4 16" />,
  image: <Icon d="M4 5h16v14H4zM4 16l4-4 4 4 3-3 5 5" />,
  brand: (
    <Icon d="M12 2l3 6 6 1-4.5 4.5L18 20l-6-3-6 3 1.5-6.5L3 9l6-1z" />
  ),
  spark: (
    <Icon d="M12 3v4m0 10v4M3 12h4m10 0h4M5.5 5.5l2.8 2.8m7.4 7.4l2.8 2.8M5.5 18.5l2.8-2.8m7.4-7.4l2.8-2.8" />
  ),
  open: <Icon d="M14 3h7v7M21 3l-9 9M10 4H4v16h16v-6" />,
  trash: (
    <Icon d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />
  ),
  filter: <Icon d="M3 4h18l-7 9v7l-4-2v-5L3 4z" />,
  ext: <Icon d="M5 5h6v14H5zM13 5h6v14h-6zM5 12h14" />,
  share: <Icon d="M16 6l-4-4-4 4M12 2v13M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />,
  users: <Icon d="M9 11a3 3 0 100-6 3 3 0 000 6zM3 20v-1a5 5 0 015-5h2a5 5 0 015 5v1M17 11a3 3 0 100-6M21 20v-1a5 5 0 00-3-4.58" />,
} as const;

/* ─── Status mapping ───────────────────────────────────────── */

export type StatusKey =
  | "pending"
  | "in-progress"
  | "done"
  | "stale"
  | "failed"
  | "skipped"
  | "awaiting-input";

interface StatusInfo {
  label: string;
  loz: string;
  dot: string;
}

export const STATUS: Record<StatusKey, StatusInfo> = {
  pending: { label: "Pending", loz: "", dot: "dot--pending" },
  "in-progress": { label: "Running", loz: "loz--run", dot: "dot--run" },
  done: { label: "Done", loz: "loz--done", dot: "dot--done" },
  stale: { label: "Stale", loz: "loz--stale", dot: "dot--stale" },
  failed: { label: "Failed", loz: "loz--failed", dot: "dot--failed" },
  skipped: { label: "Skipped", loz: "loz--stale", dot: "dot--stale" },
  "awaiting-input": { label: "Needs input", loz: "loz--stale", dot: "dot--stale" },
};

/* ─── StatusDot ────────────────────────────────────────────── */

interface StatusDotProps {
  status: string;
}

export const StatusDot: React.FC<StatusDotProps> = ({ status }) => (
  <span
    className={`dot ${STATUS[status as StatusKey]?.dot || ""}`}
    aria-label={STATUS[status as StatusKey]?.label}
  />
);

/* ─── StatusLoz ────────────────────────────────────────────── */

interface StatusLozProps {
  status: string;
  children?: React.ReactNode;
}

export const StatusLoz: React.FC<StatusLozProps> = ({ status, children }) => (
  <span
    className={`loz ${STATUS[status as StatusKey]?.loz || ""}`}
    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}
    title={typeof children === 'string' ? children : undefined}
  >
    {children || STATUS[status as StatusKey]?.label}
  </span>
);

/* ─── Station ──────────────────────────────────────────────── */

interface StationProps {
  n?: React.ReactNode;
  size?: "" | "sm" | "lg";
  done?: boolean;
  ghost?: boolean;
  /** Amber styling — used when the phase has at least one outdated step. */
  stale?: boolean;
}

export const Station: React.FC<StationProps> = ({
  n,
  size = "",
  done = false,
  ghost = false,
  stale = false,
}) => (
  <span
    className={`station${size ? ` station--${size}` : ""}${stale ? " station--stale" : done ? " station--done" : ""}${ghost ? " station--ghost" : ""}`}
  >
    {done && !stale ? I.check : n}
  </span>
);

/* ─── Progress ─────────────────────────────────────────────── */

interface ProgressProps {
  total?: number;
  done?: number;
  now?: number;
}

export const Progress: React.FC<ProgressProps> = ({
  total = 6,
  done = 0,
  now = -1,
}) => (
  <span className="progress" aria-label={`${done} of ${total} done`}>
    {Array.from({ length: total }).map((_, i) => (
      <i key={i} className={i < done ? "on" : i === now ? "now" : ""} />
    ))}
  </span>
);

/* ─── Logo ─────────────────────────────────────────────────── */

interface LogoProps {
  size?: number;
}

export const Logo: React.FC<LogoProps> = ({ size = 20 }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="4" fill="var(--ink)" />
      <path
        d="M6 17V8l3 3 3-3 3 3 3-3v9"
        stroke="var(--bg)"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="18" cy="17" r="1.5" fill="var(--accent)" />
    </svg>
    <span
      style={{
        font: "600 14.5px var(--font-sans)",
        letterSpacing: "-0.01em",
      }}
    >
      App<span style={{ color: "var(--accent-2)" }}>·</span>Factory
    </span>
  </span>
);
